/**
 * A response lost AFTER the mock handled the request — the e2e hook.
 *
 * WHY THIS EXISTS: production, 2026-10-26 (Honeybadger 135220454). The API
 * committed `POST /api/products` and logged a 201; the browser never got it and
 * reported "Could not reach the server". Every mock handler either answers or
 * does not run, so dev and e2e could produce a failed write or a successful one
 * but never the third case — a write that succeeded with nobody told. Same
 * lesson as versionConflict.ts and requestTooLarge.ts: before concluding "this
 * can't happen", check whether the mock is capable of making it happen.
 *
 * MSW runs its handlers in the PAGE, so a spec arms this from `page.evaluate`:
 *
 *   window.__mockDropResponses = [{ method: 'POST', path: '/api/cases' }]
 *
 * Each entry drops ONE matching response and is then consumed; the handler has
 * already run, so the write is stored. Matching is on the exact pathname.
 */

export interface IDroppedResponse {
  method: string
  path: string
}

type TDropWindow = { __mockDropResponses?: IDroppedResponse[] }

/** Should this request's response be dropped? Consumes the matching entry. */
export function takeDroppedResponse(request: Request): boolean {
  const queue =
    typeof window === 'undefined'
      ? undefined
      : (window as unknown as TDropWindow).__mockDropResponses
  if (!queue?.length) {
    return false
  }
  const pathname = new URL(request.url).pathname
  const index = queue.findIndex(
    (entry) =>
      entry.method.toUpperCase() === request.method.toUpperCase() &&
      entry.path === pathname
  )
  if (index === -1) {
    return false
  }
  queue.splice(index, 1)
  console.warn(
    `[api/msw/modules/droppedResponse.ts] dropping the response to` +
      ` ${request.method} ${pathname} (the handler has already run)`
  )
  return true
}
